import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getConnectedApplianceIds } from '../../Functions/ActuatorChannels';
import { ActuatorStateI } from '../types/app';
import { ConnectedActuatorChannelI, DeviceI,DevicesI,IconsI } from '../types/controllers';
import { resetConfiguration } from './appReducer';

const getInitialDevicesState = (): DevicesI => ({
    devices: [],
    selectedDevice: {
        id: 0,
        name: '',
        tech: '',
        type: '',
        material: '',
        icons: 0,
        round: false,
        color: '',
        colorShortcut: '',
        className: '',
        previewImage: '',
        renderImage: '',
        configuredImageName: '',
        state: 'error',
        iconsData: [] as IconsI[],
        connectedActuator: undefined
    }
});

const initDevicesState: DevicesI = getInitialDevicesState();

const getSlotLinks = (slot: IconsI): ConnectedActuatorChannelI[] => {
    const multiLinks: ConnectedActuatorChannelI[] = slot.connectedActuatorChannels ?? [];
    const legacyLink: ConnectedActuatorChannelI[] =
        slot.connectedActuatorId !== undefined && slot.connectedActuatorChannel !== undefined
            ? [{ actuatorId: slot.connectedActuatorId, channel: slot.connectedActuatorChannel, functionIndex: 1 }]
            : [];

    return (multiLinks.length > 0 ? multiLinks : legacyLink)
        .filter((link, index, array) =>
            array.findIndex((candidate) =>
                candidate.actuatorId === link.actuatorId &&
                candidate.channel === link.channel &&
                (candidate.functionIndex ?? 1) === (link.functionIndex ?? 1)
            ) === index
        );
};

const hasSlotAssignment = (slot: IconsI) => {
    return getSlotLinks(slot).length > 0;
};

const recomputeDeviceState = (device: Pick<DeviceI, 'type' | 'iconsData' | 'connectedActuator' | 'connectedActuatorIds' | 'thermostatRequiredActuator'>) => {
    if(device.type === 'thermostat'){
        if (device.thermostatRequiredActuator !== null && device.thermostatRequiredActuator !== undefined) {
            return (device.connectedActuatorIds ?? []).length > 0 || device.connectedActuator !== undefined
                ? 'ok' as const
                : 'error' as const;
        }

        const hasDirectApplianceConnection = device.iconsData.some((slot) => (slot.applianceId ?? []).length > 0);
        return hasDirectApplianceConnection ? 'ok' as const : 'error' as const;
    }

    // For regular controllers:
    // 'ok' - all buttons have their appliance
    // 'warning' - there's an unassigned slot for appliance
    // 'error' - no appliance is connected
    const connectedAppliances = device.iconsData.filter((slot) => hasSlotAssignment(slot));
    const totalSlots = device.iconsData.length;
    const connectedCount = connectedAppliances.length;

    if (totalSlots === 0 || connectedCount === 0) {
        return 'error' as const;
    }
    if (connectedCount === totalSlots) {
        return 'ok' as const;
    }
    return 'warning' as const;
};

const controllersSlice = createSlice({
    name: 'controllers',
    initialState: initDevicesState,
    reducers: {
        addDevice: (state, action: PayloadAction<DeviceI>) => {
            state.devices.push(action.payload);
        },
        setSelectedDeviceId: (state, action: PayloadAction<number>) => {
            state.selectedDevice.id = action.payload;
        },
        setSelectedDevice: (state, action: PayloadAction<DeviceI>) => {
            state.selectedDevice = action.payload;
        },
        setSelectedDeviceConfiguredImageName: (state, action: PayloadAction<string>) => {
            const imageName = action.payload;
            state.selectedDevice.configuredImageName = imageName;

            const deviceIndex = state.devices.findIndex((device) => device.id === state.selectedDevice.id);
            if(deviceIndex !== -1){
                state.devices[deviceIndex].configuredImageName = imageName;
            }
        },
        removeDevice: (state, action: PayloadAction<number>) => {
            const controllerId = action.payload;
            state.devices = state.devices.filter((device) => device.id !== controllerId);
            if(state.selectedDevice.id === controllerId){
                state.selectedDevice = initDevicesState.selectedDevice;
            }
        },
        setDevicePosition: (state, action: PayloadAction<{ id: number; x: number; y: number }>) => {
            const { id, x, y } = action.payload;
            const device = state.devices.find((item) => item.id === id);

            if(device){
                device.x = x;
                device.y = y;
            }

            if(state.selectedDevice.id === id){
                state.selectedDevice.x = x;
                state.selectedDevice.y = y;
            }
        },
        setSelectedDeviceIconsData: (state, action: PayloadAction<IconsI[]>) => {
            state.selectedDevice.iconsData = action.payload;

            state.selectedDevice.state = recomputeDeviceState({
                ...state.selectedDevice,
                iconsData: action.payload
            });

            const deviceIndex = state.devices.findIndex((device) => device.id === state.selectedDevice.id);
            if(deviceIndex !== -1){
                state.devices[deviceIndex].iconsData = action.payload;
                state.devices[deviceIndex].state = state.selectedDevice.state;
            }
        },
        removeApplianceFromAllControllers: (state, action: PayloadAction<number>) => {
            const applianceId = action.payload;

            state.devices = state.devices.map((device) => {
                const nextIconsData = device.iconsData.map((slot) => {
                    const nextApplianceIds = (slot.applianceId ?? []).filter((id) => id !== applianceId);
                    if(nextApplianceIds.length === (slot.applianceId ?? []).length){
                        return slot;
                    }

                    const shouldResetLinks = nextApplianceIds.length === 0;
                    return {
                        ...slot,
                        icon: nextApplianceIds.length > 0 ? slot.icon : 'add_icon',
                        applianceId: nextApplianceIds.length > 0 ? nextApplianceIds : undefined,
                        connectedActuatorChannels: shouldResetLinks ? undefined : slot.connectedActuatorChannels,
                        connectedActuatorId: shouldResetLinks ? undefined : slot.connectedActuatorId,
                        connectedActuatorChannel: shouldResetLinks ? undefined : slot.connectedActuatorChannel
                    };
                });

                const nextState = recomputeDeviceState({ ...device, iconsData: nextIconsData });

                if(device.id === state.selectedDevice.id){
                    state.selectedDevice.iconsData = nextIconsData;
                    state.selectedDevice.state = nextState;
                }

                return {
                    ...device,
                    iconsData: nextIconsData,
                    state: nextState
                };
            });
        },
        setSelectedDevicePosition: (state, action: PayloadAction<{ x: number; y: number }>) => {
            const { x, y } = action.payload;
            state.selectedDevice.x = x;
            state.selectedDevice.y = y;
        },
        resetSelectedDevicePosition: (state) => {
            state.selectedDevice.x = 50;
            state.selectedDevice.y = 50;
        },
        setControllerConnectedActuator: (state, action: PayloadAction<{ controllerId: number; actuatorIds?: number[] }>) => {
            const device = state.devices.find((d) => d.id === action.payload.controllerId);
            if (device) {
                device.connectedActuatorIds = action.payload.actuatorIds;
                device.connectedActuator = action.payload.actuatorIds?.[0];
                device.state = recomputeDeviceState(device);
            }
            if (state.selectedDevice.id === action.payload.controllerId) {
                state.selectedDevice.connectedActuatorIds = action.payload.actuatorIds;
                state.selectedDevice.connectedActuator = action.payload.actuatorIds?.[0];
                state.selectedDevice.state = recomputeDeviceState(state.selectedDevice);
            }
        },
        syncControllerChannelConnections: (state, action: PayloadAction<ActuatorStateI[]>) => {
            const actuators = action.payload;

            const getChannelAppliances = (actuatorId?: number, channel?: number) => {
                if (actuatorId === undefined || channel === undefined) {
                    return [] as number[];
                }

                const actuator = actuators.find((item) => item.id === actuatorId);
                if (!actuator) {
                    return [] as number[];
                }

                const channelConnection = (actuator.connectedAppliancesByChannel ?? [])
                    .find((connection) => connection.channel === channel);

                if (!channelConnection) {
                    return [] as number[];
                }

                return getConnectedApplianceIds({
                    ...actuator,
                    connectedAppliancesByChannel: [channelConnection]
                });
            };

            state.devices = state.devices.map((device) => {
                const nextIconsData = device.iconsData.map((slot) => {
                    const links = getSlotLinks(slot);

                    if (links.length === 0) {
                        return slot;
                    }

                    const nextApplianceIds = Array.from(new Set(
                        links.flatMap((link) => getChannelAppliances(link.actuatorId, link.channel))
                    ));

                    if (nextApplianceIds.length === 0) {
                        return {
                            ...slot,
                            applianceId: undefined,
                            connectedActuatorChannels: links,
                            connectedActuatorId: links[0]?.actuatorId,
                            connectedActuatorChannel: links[0]?.channel
                        };
                    }

                    return {
                        ...slot,
                        applianceId: nextApplianceIds,
                        connectedActuatorChannels: links,
                        connectedActuatorId: links[0]?.actuatorId,
                        connectedActuatorChannel: links[0]?.channel
                    };
                });

                const nextState = recomputeDeviceState({ ...device, iconsData: nextIconsData });

                if (device.id === state.selectedDevice.id) {
                    state.selectedDevice.iconsData = nextIconsData;
                    state.selectedDevice.state = nextState;
                }

                return {
                    ...device,
                    iconsData: nextIconsData,
                    state: nextState
                };
            });
        },
    },
    extraReducers: (builder) => {
        builder.addCase(resetConfiguration, () => getInitialDevicesState());
    }
});

export const { 
    addDevice,
    setSelectedDeviceId,
    setSelectedDevice,
    setSelectedDeviceConfiguredImageName,
    removeDevice,
    setDevicePosition,
    removeApplianceFromAllControllers,
    setSelectedDeviceIconsData,
    setSelectedDevicePosition,
    resetSelectedDevicePosition,
    setControllerConnectedActuator,
    syncControllerChannelConnections
} = controllersSlice.actions;

export default controllersSlice.reducer;